import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  IconButton,
  useTheme,
  Skeleton,
  Link,
  CircularProgress,
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DownloadIcon from "@mui/icons-material/Download";
import { format } from "date-fns";
import { toast } from "react-toastify";
import { Message, MessageTypeColor } from "../../../../../types";
import { ReactionAndFavorite } from "../../reaction-and-favorite/reaction-and-favorite";
import { useImageCollectionStyles } from "./styles";
import { useAppStore } from "../../../../../stores/appStore";
import { motion } from "framer-motion";
import { ImageSwiperModal } from "../image-swiper-modal/image-swiper-modal";
import { CustomMarkdown } from "../../../../custom-markdown/custom-markdown";
import {
  downloadImageProxy,
  downloadAllImagesAsZip,
} from "../../../../../axios/downloadImage";
import { useClampedGalleryIndex } from "../../../../../hooks/useClampedGalleryIndex";
import { useAuthenticatedFileUrl } from "../../../../../hooks/useAuthenticatedFileUrl";

interface ImageCollectionProps {
  message: Message;
  isBot: boolean;
  variant: MessageTypeColor;
  currentReaction?: string;
  isSaved?: boolean;
  markedIds: Record<string, string>;
  isTags?: string[];
  handleReactionClick: (reactionKey: string) => void;
  handleAddToFavorite: () => void;
  handleRemoveTag: (tag: string) => Promise<void>;
  nextMessage?: Message;
  setLargeHigth: React.Dispatch<React.SetStateAction<boolean>>;
  // When true, hide ReactionAndFavorite and timestamp (used inside TabbedMessage)
  embedded?: boolean;
}

export const ImageCollection: React.FC<ImageCollectionProps> = ({
  message,
  isBot,
  variant,
  currentReaction,
  isSaved,
  markedIds,
  handleReactionClick,
  handleAddToFavorite,
  isTags,
  handleRemoveTag,
  nextMessage,
  setLargeHigth,
  embedded = false,
}) => {
  const { isDark } = useAppStore();
  const theme = useTheme();
  const styles = useImageCollectionStyles(isBot, variant, embedded)(theme);

  const [isFullScreenOpen, setIsFullScreenOpen] = useState(false);
  const [expandedDescriptions, setExpandedDescriptions] = useState<
    Record<number, boolean>
  >({});
  const [imagesLoaded, setImagesLoaded] = useState<boolean[]>([]);
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  const [isDownloading, setIsDownloading] = useState<Record<number, boolean>>(
    {},
  );
  const [isDownloadSectionExpanded, setIsDownloadSectionExpanded] =
    useState(false);
  const [isTitleExpanded, setIsTitleExpanded] = useState(false);
  const imageRefs = useRef<(HTMLImageElement | null)[]>([]);

  const title = message.title || "";
  const images = useMemo(
    () => message.image_collection || [],
    [message.image_collection],
  );

  const { safeIndex, setCurrentImageIndex } = useClampedGalleryIndex(
    images.length,
  );
  const currentImageUrl = images[safeIndex]?.url ?? "";
  const { url: resolvedCurrentImageUrl } =
    useAuthenticatedFileUrl(currentImageUrl);

  useEffect(() => {
    if (images.length > 0) {
      setImagesLoaded(new Array(images.length).fill(false));
      imageRefs.current = new Array(images.length).fill(null);

      const expandedState: Record<number, boolean> = {};
      images.forEach((_, index) => {
        expandedState[index] = true;
      });
      setExpandedDescriptions(expandedState);
    }
  }, [images]);

  if (!images.length) return null;

  const handleNextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const handlePrevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleImageClick = () => {
    setIsFullScreenOpen(true);
  };

  const handleCloseFullScreen = () => {
    setIsFullScreenOpen(false);
  };

  const handleImageLoad = (index: number, imgElement: HTMLImageElement) => {
    const newImagesLoaded = [...imagesLoaded];
    newImagesLoaded[index] = true;
    setImagesLoaded(newImagesLoaded);
    // Store ref when image loads
    imageRefs.current[index] = imgElement;
  };

  const toggleDescription = (event: React.MouseEvent) => {
    event.stopPropagation();
    setExpandedDescriptions((prev) => ({
      ...prev,
      [safeIndex]: !prev[safeIndex],
    }));
  };

  const toggleDownloadSection = (event: React.MouseEvent) => {
    event.stopPropagation();
    setIsDownloadSectionExpanded((prev) => !prev);
  };

  const toggleTitle = (event: React.MouseEvent) => {
    event.stopPropagation();
    setIsTitleExpanded((prev) => !prev);
  };

  const sanitizeFilename = (name: string): string => {
    return name
      .replace(/[/\\:*?"<>|]/g, "_")
      .replace(/\s+/g, " ")
      .trim();
  };

  const extractDocumentName = (text?: string | null): string | null => {
    if (!text) return null;
    const trimmed = text.trim();
    // Try to find pattern like "Document: <name>.pdf"
    const docMatch = trimmed.match(/Document:\s*([^\n]*?\.pdf)/i);
    if (docMatch?.[1]) {
      return docMatch[1].trim().replace(/\s+/g, " ");
    }
    // Fallback: any "<name>.pdf" in the line
    const anyPdfMatch = trimmed.match(/([^\n]*?\.pdf)/i);
    if (anyPdfMatch?.[1]) {
      return anyPdfMatch[1].trim().replace(/\s+/g, " ");
    }
    return null;
  };

  const getImageDisplayName = (index: number): string => {
    const desc = images[index]?.description ?? "";
    const t = title ?? "";
    const fromDesc = extractDocumentName(desc);
    const fromTitle = extractDocumentName(t);
    const rawName = fromDesc || fromTitle;
    const name = rawName
      ? rawName.replace(/\.(pdf|jpg|jpeg|png)$/i, "")
      : null;
    if (name) return name;
    return `Download image ${index + 1}`;
  };

  const getImageFilename = (index: number): string => {
    const displayName = getImageDisplayName(index);
    if (displayName.startsWith("Download image ")) {
      const base = title
        ? title.replace(/[^a-z0-9]/gi, "_").toLowerCase()
        : "image";
      return `${base}_${(index + 1).toString().padStart(2, "0")}.jpg`;
    }
    const sanitized = sanitizeFilename(displayName);
    const base = sanitized || `image_${(index + 1).toString().padStart(2, "0")}`;
    return base.endsWith(".jpg") ? base : `${base}.jpg`;
  };

  const handleDownloadImage = async (e: React.MouseEvent, index: number) => {
    e.stopPropagation();
    if (isDownloading[index]) return;

    try {
      setIsDownloading((prev) => ({ ...prev, [index]: true }));
      const imageUrl = images[index].url;
      const filename = getImageFilename(index);

      const blob = await downloadImageProxy(imageUrl, filename);

      // Create download link
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
      }, 100);

      toast.success(`Image "${filename}" downloaded successfully`, {
        position: "top-right",
        autoClose: 3000,
        closeOnClick: true,
        pauseOnHover: true,
      });
    } catch (error) {
      console.error("Failed to download image:", error);
      const imageUrl = images[index].url;
      window.open(imageUrl, "_blank", "noopener,noreferrer");
    } finally {
      setIsDownloading((prev) => ({ ...prev, [index]: false }));
    }
  };

  const handleDownloadAll = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isDownloadingAll) return;

    try {
      setIsDownloadingAll(true);
      const baseFilename = title
        ? title.replace(/[^a-z0-9]/gi, "_").toLowerCase()
        : "images";
      const zipFilename = `${baseFilename}.zip`;

      const imageUrls = images.map((img) => img.url);

      const blob = await downloadAllImagesAsZip(imageUrls, baseFilename);

      // Create download link for ZIP
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = zipFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
      }, 100);

      toast.success(
        `All ${images.length} images downloaded as "${zipFilename}"`,
        {
          position: "top-right",
          autoClose: 3000,
          closeOnClick: true,
          pauseOnHover: true,
        },
      );
    } catch (error) {
      console.error("Failed to download images as ZIP:", error);
    } finally {
      setIsDownloadingAll(false);
    }
  };

  const renderDescription = (description: string) => {
    if (!description) return null;

    const isExpanded = expandedDescriptions[safeIndex] !== false;
    const isLongDescription = description.length > 100;

    if (isLongDescription && !isExpanded) {
      return (
        <Box sx={styles.descriptionContainer}>
          <Typography
            variant="body2"
            color={isDark ? "common.white" : "text.primary"}
          >
            {description.substring(0, 100)}...
          </Typography>
          <Box sx={styles.descriptionCollapsedContainer}>
            <Box sx={styles.collapseToggle} onClick={toggleDescription}>
              <Typography variant="body2" sx={styles.collapseToggleText}>
                More
              </Typography>
              <ExpandMoreIcon sx={styles.collapseToggleIcon(true)} />
            </Box>
          </Box>
        </Box>
      );
    }

    return (
      <Box sx={styles.descriptionFullContainer}>
        <CustomMarkdown>{description}</CustomMarkdown>
        {isLongDescription && (
          <Box sx={styles.descriptionCollapsedContainer}>
            <Box sx={styles.collapseToggle} onClick={toggleDescription}>
              <Typography variant="body2" sx={styles.collapseToggleText}>
                Less
              </Typography>
              <ExpandMoreIcon sx={styles.collapseToggleIcon(false)} />
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
      },
    },
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.3 },
    },
  };

  const isLongTitle = title.length > 220;

  return (
    <Box sx={styles.container}>
      {title && (
        <Box sx={styles.titleRow}>
          <Box
            sx={
              isLongTitle && !isTitleExpanded
                ? styles.titleCollapsedTypography
                : styles.titleTypography
            }
          >
            <CustomMarkdown skipHtml>{title}</CustomMarkdown>
          </Box>
          {isLongTitle && (
            <Box sx={styles.titleToggleContainer}>
              <Box sx={styles.collapseToggle} onClick={toggleTitle}>
                <Typography variant="body2" sx={styles.collapseToggleText}>
                  {isTitleExpanded ? "Less" : "More"}
                </Typography>
                <ExpandMoreIcon sx={styles.collapseToggleIcon(!isTitleExpanded)} />
              </Box>
            </Box>
          )}
        </Box>
      )}

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        style={{ width: "100%" } as React.CSSProperties}
      >
        <Box sx={styles.imageWrapper}>
          <motion.div
            key={safeIndex}
            variants={imageVariants}
            initial="hidden"
            animate="visible"
          >
            <Box sx={styles.imageContainer} onClick={handleImageClick}>
              {!imagesLoaded[safeIndex] && (
                <Skeleton
                  variant="rectangular"
                  animation="wave"
                  sx={styles.skeleton}
                />
              )}
              <Box
                component="img"
                ref={(el: HTMLImageElement | null) => {
                  if (el) {
                    imageRefs.current[safeIndex] = el;
                  }
                }}
                src={resolvedCurrentImageUrl || currentImageUrl}
                alt={images[safeIndex]?.description || "Image"}
                sx={{
                  ...styles.image,
                  visibility: imagesLoaded[safeIndex] ? "visible" : "hidden",
                }}
                onLoad={(e) => {
                  const imgElement = e.currentTarget as HTMLImageElement;
                  handleImageLoad(safeIndex, imgElement);
                }}
              />
              <Box sx={styles.imageCounter}>
                {safeIndex + 1} / {images.length}
              </Box>
            </Box>
          </motion.div>

          {images.length > 1 && (
            <>
              <IconButton
                sx={styles.navigationButton}
                className="prev"
                onClick={handlePrevImage}
                size="small"
              >
                <NavigateBeforeIcon />
              </IconButton>
              <IconButton
                sx={styles.navigationButton}
                className="next"
                onClick={handleNextImage}
                size="small"
              >
                <NavigateNextIcon />
              </IconButton>
            </>
          )}
        </Box>

        <Box sx={styles.descriptionWrapper}>
          {renderDescription(images[safeIndex]?.description ?? "")}
        </Box>

        {/* Download Section */}
        <Box sx={styles.downloadSection}>
          <Box sx={styles.downloadContainer}>
            <Box
              sx={styles.downloadHeader}
              onClick={toggleDownloadSection}
              style={{ cursor: "pointer" }}
            >
              <Typography variant="body2" sx={styles.downloadHeaderText}>
                Download images
              </Typography>
              <ExpandMoreIcon
                sx={styles.collapseToggleIcon(!isDownloadSectionExpanded)}
              />
            </Box>

            {/* Animated download links */}
            {isDownloadSectionExpanded && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                style={{ overflow: "hidden" }}
              >
                {/* Current page download link */}
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.1 }}
                >
                  <Box sx={styles.downloadLinkContainer}>
                    <Link
                      component="button"
                      onClick={(e) => handleDownloadImage(e, safeIndex)}
                      disabled={isDownloading[safeIndex]}
                      sx={styles.downloadLink(!!isDownloading[safeIndex])}
                    >
                      {isDownloading[safeIndex] ? (
                        <CircularProgress
                          size={16}
                          sx={styles.downloadProgress}
                        />
                      ) : (
                        <DownloadIcon sx={styles.downloadLinkIcon} />
                      )}
                      <Typography variant="body2" sx={styles.downloadLinkText}>
                        {isDownloading[safeIndex]
                          ? "Downloading..."
                          : getImageDisplayName(safeIndex)}
                      </Typography>
                    </Link>
                  </Box>
                </motion.div>

                {/* Download all link */}
                {images.length > 1 && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.2 }}
                  >
                    <Link
                      component="button"
                      onClick={handleDownloadAll}
                      disabled={isDownloadingAll}
                      sx={styles.downloadAllLink(isDownloadingAll)}
                    >
                      {isDownloadingAll ? (
                        <CircularProgress
                          size={16}
                          sx={styles.downloadProgress}
                        />
                      ) : (
                        <DownloadIcon sx={styles.downloadLinkIcon} />
                      )}
                      <Typography variant="body2" sx={styles.downloadLinkText}>
                        {isDownloadingAll
                          ? "Downloading..."
                          : "Download all as ZIP"}
                      </Typography>
                    </Link>
                  </motion.div>
                )}
              </motion.div>
            )}
          </Box>
        </Box>

        {!embedded && (
          <Box sx={styles.bottomContainer}>
            <ReactionAndFavorite
              currentReaction={currentReaction}
              markedIds={markedIds}
              isSaved={isSaved}
              isTags={isTags}
              handleReactionClick={handleReactionClick}
              handleAddToFavorite={handleAddToFavorite}
              reactionBg={styles.container.background}
              handleRemoveTag={handleRemoveTag}
            />
            <Typography variant="caption" sx={styles.timestamp}>
              <span>{format(new Date(message.date), "dd/MM/yy")}</span>
              <span style={{ margin: "0 5px" }} />
              <span>{format(new Date(message.date), "HH:mm")}</span>
            </Typography>
          </Box>
        )}
      </motion.div>

      <ImageSwiperModal
        open={isFullScreenOpen}
        onClose={handleCloseFullScreen}
        images={images}
        initialIndex={safeIndex}
      />
    </Box>
  );
};
